import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';
import { clearTextSelection } from '../utils/clearTextSelection';

export const ROBOT_COMMAND_SELECT_EVENT = 'robot.command.select';

const SERVER_URL = window.location.hostname.includes('devtunnels.ms')
  ? `https://${window.location.hostname}/api`
  : '/api';

const RobotCommandsContext = createContext(null);

const formatNumber = (num, digits = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatProgress = (complete, need) =>
  `${formatNumber(complete, 0)} / ${formatNumber(need, 0)}`;

const formatCreatedAt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Проценты в таблице робота: нет значения — пусто; 0 и остальные числа показываем */
const formatRobotPercent = (num, digits = 1) => {
  if (num === null || num === undefined || num === '') return '';
  const n = Number(num);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const brokerLabels = {
  'bcs': 'БКС',
  'finam': 'Финам',
};
const formatBroker = (broker) => {
  return brokerLabels[broker] ?? broker;
};

const IconButton = ({ onClick, title, children, disabled }) => (
  <button
    type="button"
    className="robot-cmd-icon-btn"
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      ...styles.iconBtn,
      opacity: disabled ? 0.4 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    {children}
  </button>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const StopIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M6 6h12v12H6z" />
  </svg>
);

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <rect x="0" y="2" width="24" height="2" />
    <rect x="0" y="4" width="2" height="3" />
    <rect x="22" y="4" width="2" height="3" />
    <rect x="0" y="7" width="24" height="2" />
    <rect x="2" y="9" width="2" height="11" />
    <rect x="20" y="9" width="2" height="11" />
    <rect x="2" y="20" width="20" height="2" />
    <rect x="9" y="11" width="6" height="2" />
  </svg>
);

const compactColumns = [
  { key: 'broker', title: 'Брокер', width: '8%' },
  { key: 'stock', title: 'Акция', width: '11%' },
  { key: 'futures', title: 'Фьюч', width: '15%' },
  { key: 'progress', title: 'Прогресс', width: '22%' },
  { key: 'spread', title: 'Спред %', width: '10%' },
  { key: 'percentDo', title: 'ДО %', width: '10%' },
  { key: 'percentExp', title: 'Эксп %', width: '10%' },
  { key: 'actions', title: '', width: '70px' },
];

const allCommandsColumns = [
  { key: 'createdAt', title: 'Создано', width: '14%' },
  { key: 'broker', title: 'Брокер', width: '8%' },
  { key: 'stock', title: 'Акция', width: '10%' },
  { key: 'futures', title: 'Фьюч', width: '14%' },
  { key: 'progress', title: 'Прогресс', width: '20%' },
  { key: 'spread', title: 'Спред %', width: '11%' },
  { key: 'percentDo', title: 'ДО %', width: '11%' },
  { key: 'percentExp', title: 'Эксп %', width: '12%' },
];

const pairColumn = { key: 'pair', title: 'Пара' };
const statusColumn = { key: 'status', title: '' };

const getVisibleColumns = ({ showCreatedAt, groupByBroker, mergePairColumn, showStatusColumn }) => {
  let columns = showCreatedAt ? [...allCommandsColumns] : [...compactColumns];

  if (groupByBroker) {
    columns = columns.filter((column) => column.key !== 'broker');
  }

  if (mergePairColumn) {
    columns = columns.filter((column) => column.key !== 'stock' && column.key !== 'futures');
    const progressIndex = columns.findIndex((column) => column.key === 'progress');
    columns.splice(progressIndex === -1 ? 0 : progressIndex, 0, pairColumn);
  }

  if (showStatusColumn) {
    columns = columns.filter((column) => column.key !== 'actions');
    columns.push(statusColumn);
  }

  return columns;
};

const renderColGroup = (visibleColumns, fluidColumns) => {
  if (fluidColumns) {
    return null;
  }
  return (
    <colgroup>
      {visibleColumns.map((column) => (
        <col key={column.key} style={{ width: column.width }} />
      ))}
    </colgroup>
  );
};

const sideSortOrder = { enter: 0, exit: 1 };
const brokerSortOrder = { bcs: 0, finam: 1 };

const emptyActionLoading = new Set();
const noop = () => {};

const sortCommands = (commands) =>
  [...commands].sort(
    (a, b) => (sideSortOrder[a.side] ?? 2) - (sideSortOrder[b.side] ?? 2),
  );

const sortCommandsByBroker = (commands) =>
  [...commands].sort((a, b) => {
    const brokerDiff = (brokerSortOrder[a.broker] ?? 99) - (brokerSortOrder[b.broker] ?? 99);
    if (brokerDiff !== 0) {
      return brokerDiff;
    }
    return (sideSortOrder[a.side] ?? 2) - (sideSortOrder[b.side] ?? 2);
  });

const ROBOT_ROW_LONG_PRESS_MS = 450;

const getStatusDotClassName = (status) => {
  switch (status) {
    case 'inprogress':
      return 'robot-cmd-status-dot robot-cmd-status-dot--inprogress';
    case 'paused':
      return 'robot-cmd-status-dot robot-cmd-status-dot--paused';
    case 'completed':
      return 'robot-cmd-status-dot robot-cmd-status-dot--completed';
    case 'error':
      return 'robot-cmd-status-dot robot-cmd-status-dot--error';
    default:
      return 'robot-cmd-status-dot robot-cmd-status-dot--stopped';
  }
};

const getStatusDotTitle = (status, errorMsg) => {
  switch (status) {
    case 'inprogress':
      return 'В работе';
    case 'paused':
      return 'Пауза';
    case 'completed':
      return 'Завершено';
    case 'error':
      return errorMsg ? String(errorMsg) : 'Ошибка';
    case 'stopped':
      return 'Остановлено';
    default:
      return status || 'Статус';
  }
};

function RobotCommandActionBar({ command, actionLoading, runAction }) {
  if (!command) {
    return null;
  }

  const isLoading = actionLoading.has(command.id);
  const isActive = command.status === 'inprogress';
  const isPaused = command.status === 'paused';
  const isStopped = command.status === 'stopped';
  const isCompleted = command.status === 'completed';

  return (
    <div className="robot-cmd-action-bar" role="toolbar" aria-label="Управление автосделкой">
      <IconButton
        onClick={() => runAction(command.id, 'play')}
        title="Запустить"
        disabled={isLoading || isActive || isStopped || isCompleted}
      >
        <PlayIcon />
      </IconButton>
      <IconButton
        onClick={() => runAction(command.id, 'pause')}
        title="Приостановить"
        disabled={isLoading || isPaused || isStopped || isCompleted}
      >
        <PauseIcon />
      </IconButton>
      <IconButton
        onClick={() => runAction(command.id, 'stop')}
        title={isStopped || isCompleted ? 'Удалить' : 'Остановить и удалить'}
        disabled={isLoading}
      >
        <StopIcon />
      </IconButton>
    </div>
  );
}

const statusLegendItems = [
  { status: 'inprogress', label: 'В работе' },
  { status: 'paused', label: 'Пауза' },
  { status: 'completed', label: 'Завершено' },
];

function RobotCommandStatusLegend() {
  return (
    <div className="robot-cmd-status-legend" aria-label="Легенда статусов">
      {statusLegendItems.map((item) => (
        <span key={item.status} className="robot-cmd-status-legend__item">
          <span
            className={getStatusDotClassName(item.status)}
            aria-hidden="true"
          />
          <span className="robot-cmd-status-legend__label">{item.label}</span>
        </span>
      ))}
    </div>
  );
}

const renderCommandCell = (cmd, column, {
  isLoading,
  isActive,
  isPaused,
  isStopped,
  isCompleted,
  runAction,
}) => {
  switch (column.key) {
    case 'createdAt':
      return formatCreatedAt(cmd.createdAt);
    case 'broker':
      return formatBroker(cmd.broker);
    case 'stock':
      return cmd.stock || '-';
    case 'futures':
      return cmd.futures || '-';
    case 'pair':
      return (
        <>
          {cmd.stock || '-'}
          <br />
          ({cmd.futures || '-'})
        </>
      );
    case 'progress':
      return formatProgress(cmd.summComplete, cmd.summNeed);
    case 'spread':
      return formatRobotPercent(cmd.spread, 2);
    case 'percentDo':
      return formatRobotPercent(cmd.percentDo, 1);
    case 'percentExp':
      return formatRobotPercent(cmd.percentExp, 1);
    case 'status':
      return (
        <span
          className={getStatusDotClassName(cmd.status)}
          title={getStatusDotTitle(cmd.status, cmd.errorMsg)}
          aria-label={getStatusDotTitle(cmd.status, cmd.errorMsg)}
        />
      );
    case 'actions':
      return (
        <>
          <IconButton
            onClick={() => runAction(cmd.id, 'play')}
            title="Запустить"
            disabled={isLoading || isActive || isStopped || isCompleted}
          >
            <PlayIcon />
          </IconButton>
          <IconButton
            onClick={() => runAction(cmd.id, 'pause')}
            title="Приостановить"
            disabled={isLoading || isPaused || isStopped || isCompleted}
          >
            <PauseIcon />
          </IconButton>
          <IconButton
            onClick={() => runAction(cmd.id, 'stop')}
            title={isStopped || isCompleted ? 'Удалить' : 'Остановить и удалить'}
            disabled={isLoading}
          >
            <StopIcon />
          </IconButton>
        </>
      );
    default:
      return '-';
  }
};

function RobotCommandsGrid({
  commands,
  actionLoading = emptyActionLoading,
  runAction = noop,
  onCommandRowClick,
  onSelectCommand,
  onNavigateCommand,
  selectedCommandId = null,
  selectionMode = false,
  bodyMaxHeight = '130px',
  showCreatedAt = false,
  sortBySide = true,
  groupByBroker = false,
  fluidColumns = false,
  mergePairColumn = false,
  showStatusColumn = false,
}) {
  const rowLongPressRef = React.useRef({ timer: null, triggered: false, commandId: null });
  const visibleColumns = getVisibleColumns({
    showCreatedAt,
    groupByBroker,
    mergePairColumn,
    showStatusColumn,
  });
  const displayedCommands = useMemo(() => {
    if (groupByBroker) {
      return sortCommandsByBroker(commands);
    }
    if (sortBySide) {
      return sortCommands(commands);
    }
    return commands;
  }, [commands, groupByBroker, sortBySide]);

  const clearRowLongPress = useCallback(() => {
    const pressState = rowLongPressRef.current;
    if (pressState.timer) {
      clearTimeout(pressState.timer);
      pressState.timer = null;
    }
  }, []);

  useEffect(() => () => clearRowLongPress(), [clearRowLongPress]);

  const handleRowTouchStart = useCallback((cmd) => {
    if (!selectionMode || !onNavigateCommand) {
      return;
    }

    const pressState = rowLongPressRef.current;
    pressState.triggered = false;
    pressState.commandId = cmd.id;
    clearRowLongPress();
    pressState.timer = window.setTimeout(() => {
      pressState.timer = null;
      pressState.triggered = true;
      clearTextSelection();
      onNavigateCommand(cmd);
    }, ROBOT_ROW_LONG_PRESS_MS);
  }, [selectionMode, onNavigateCommand, clearRowLongPress]);

  const handleRowTouchEnd = useCallback(() => {
    clearRowLongPress();
  }, [clearRowLongPress]);

  const handleRowClick = useCallback((cmd) => {
    if (selectionMode) {
      const pressState = rowLongPressRef.current;
      if (pressState.triggered && pressState.commandId === cmd.id) {
        pressState.triggered = false;
        return;
      }
      onSelectCommand?.(cmd);
      return;
    }

    onCommandRowClick?.(cmd);
  }, [selectionMode, onSelectCommand, onCommandRowClick]);

  const renderHeaderCells = () =>
    visibleColumns.map((column) => (
      <th
        key={column.key}
        className={[
          'robot-cmd-th',
          column.key === 'actions' ? 'robot-cmd-th--actions' : '',
          column.key === 'pair' ? 'robot-cmd-th--pair' : '',
          column.key === 'status' ? 'robot-cmd-th--status' : '',
        ].filter(Boolean).join(' ') || undefined}
        style={!fluidColumns && column.key === 'actions' ? { textAlign: 'center' } : undefined}
      >
        {column.title}
      </th>
    ));

  const renderDataRow = (cmd) => {
    const isLoading = actionLoading.has(cmd.id);
    const isActive = cmd.status === 'inprogress';
    const isPaused = cmd.status === 'paused';
    const isStopped = cmd.status === 'stopped';
    const isCompleted = cmd.status === 'completed';
    const isError = cmd.status === 'error';
    const errorTip = isError && cmd.errorMsg ? String(cmd.errorMsg) : undefined;
    const isRowInteractive = selectionMode || Boolean(onCommandRowClick);
    const isSelected = selectionMode && selectedCommandId === cmd.id;
    const trClassName = [
      isRowInteractive ? 'robot-command-row--clickable' : '',
      isSelected ? 'robot-cmd-row--selected' : '',
      cmd.side === 'enter' ? 'robot-cmd-row--enter' : '',
      cmd.side === 'exit' ? 'robot-cmd-row--exit' : '',
      isError ? 'robot-cmd-row--error' : '',
    ].filter(Boolean).join(' ');

    return (
      <tr
        key={cmd.id}
        className={trClassName || undefined}
        style={isRowInteractive ? { cursor: 'pointer' } : undefined}
        title={errorTip}
        onClick={isRowInteractive ? () => handleRowClick(cmd) : undefined}
        onTouchStart={selectionMode ? () => handleRowTouchStart(cmd) : undefined}
        onTouchEnd={selectionMode ? handleRowTouchEnd : undefined}
        onTouchMove={selectionMode ? handleRowTouchEnd : undefined}
        onTouchCancel={selectionMode ? handleRowTouchEnd : undefined}
        onContextMenu={selectionMode ? (event) => event.preventDefault() : undefined}
        onSelectStart={selectionMode ? preventModalOverlaySelection : undefined}
      >
        {visibleColumns.map((column) => {
          const cellClassName = [
            'robot-cmd-td',
            column.key === 'actions' ? 'robot-cmd-td--actions' : '',
            column.key === 'createdAt' ? 'robot-cmd-td--created-at' : '',
            column.key === 'pair' ? 'robot-cmd-td--pair' : '',
            column.key === 'status' ? 'robot-cmd-td--status' : '',
          ].filter(Boolean).join(' ');
          return (
            <td
              key={column.key}
              className={cellClassName}
              title={errorTip}
              onClick={column.key === 'actions' ? (e) => e.stopPropagation() : undefined}
            >
              {renderCommandCell(cmd, column, {
                isLoading,
                isActive,
                isPaused,
                isStopped,
                isCompleted,
                runAction,
              })}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderBodyRows = () => {
    if (displayedCommands.length === 0) {
      return (
        <tr>
          <td colSpan={visibleColumns.length} className="robot-cmd-empty">
            Нет команд
          </td>
        </tr>
      );
    }

    return displayedCommands.map((cmd, index) => {
      const showBrokerDivider = groupByBroker
        && (index === 0 || cmd.broker !== displayedCommands[index - 1].broker);

      if (!showBrokerDivider) {
        return renderDataRow(cmd);
      }

      return (
        <React.Fragment key={cmd.id}>
          <tr className="robot-cmd-broker-divider">
            <td colSpan={visibleColumns.length}>{formatBroker(cmd.broker)}</td>
          </tr>
          {renderDataRow(cmd)}
        </React.Fragment>
      );
    });
  };

  if (fluidColumns) {
    return (
      <div
        className="robot-cmd-table-body robot-cmd-table-body--fluid"
        style={{ maxHeight: bodyMaxHeight }}
      >
        <table className="robot-cmd-table robot-cmd-table--fluid">
          <thead>
            <tr>{renderHeaderCells()}</tr>
          </thead>
          <tbody>{renderBodyRows()}</tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="robot-cmd-table-wrapper">
      <table className="robot-cmd-table">
        {renderColGroup(visibleColumns, fluidColumns)}
        <thead>
          <tr>{renderHeaderCells()}</tr>
        </thead>
      </table>
      <div className="robot-cmd-table-body" style={{ maxHeight: bodyMaxHeight }}>
        <table className="robot-cmd-table">
          {renderColGroup(visibleColumns, fluidColumns)}
          <tbody>{renderBodyRows()}</tbody>
        </table>
      </div>
    </div>
  );
}

function RobotCommandsAllModal({ isOpen, onClose, commands, loading, error }) {
  useModalSelectionGuard(isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="secrets-modal-overlay" onClick={onClose} onSelectStart={preventModalOverlaySelection}>
      <div
        className="robot-commands-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="robot-commands-modal-header">
          <span>Все автосделки (за три месяца)</span>
          <button
            type="button"
            className="robot-commands-modal-close"
            onClick={onClose}
            title="Закрыть"
          >
            ×
          </button>
        </div>
        {loading && (
          <div style={styles.modalMessage}>Загрузка...</div>
        )}
        {error && (
          <div style={styles.error}>{error}</div>
        )}
        {!loading && (
          <RobotCommandsGrid
            commands={commands}
            bodyMaxHeight="min(60vh, 480px)"
            showCreatedAt
            sortBySide={false}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

export function RobotCommandsProvider({ children }) {
  const [commands, setCommands] = useState([]);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(new Set());

  const fetchCommands = useCallback(async () => {
    try {
      const res = await authService.authenticatedRequest(SERVER_URL + '/v1/robot/commands');
      const data = await res.json();
      setCommands(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.message);
      setCommands([]);
    }
  }, []);

  useEffect(() => {
    fetchCommands();
    const onUpdate = (event) => {
      const next = event?.detail;
      setCommands(Array.isArray(next) ? next : []);
      setError(null);
    };
    window.addEventListener('robot.commands.update', onUpdate);
    return () => {
      window.removeEventListener('robot.commands.update', onUpdate);
    };
  }, [fetchCommands]);

  const runAction = useCallback(async (commandId, action) => {
    if (actionLoading.has(commandId)) return;
    setActionLoading((prev) => new Set(prev).add(commandId));
    try {
      const endpoint = {
        play: 'work-command',
        pause: 'pause-command',
        stop: 'stop-command',
      }[action];
      await authService.authenticatedRequest(SERVER_URL + '/v1/robot/' + endpoint + '?commandId=' + commandId, {
        method: 'POST',
      });
      await fetchCommands();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading((prev) => {
        const next = new Set(prev);
        next.delete(commandId);
        return next;
      });
    }
  }, [actionLoading, fetchCommands]);

  return (
    <RobotCommandsContext.Provider value={{ commands, error, actionLoading, runAction, fetchCommands }}>
      {children}
    </RobotCommandsContext.Provider>
  );
}

function RobotCommandsTable({
  variant = 'floating',
  showWhenEmpty = false,
  bodyMaxHeight,
  groupByBroker = false,
  fluidColumns = false,
  mergePairColumn = false,
  mobileSelectionLayout = false,
}) {
  const robotCommands = useContext(RobotCommandsContext);
  const commands = robotCommands?.commands || [];
  const error = robotCommands?.error;
  const actionLoading = robotCommands?.actionLoading || new Set();
  const runAction = robotCommands?.runAction || (() => {});
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedCommandId, setSelectedCommandId] = useState(null);

  const [isAllModalOpen, setIsAllModalOpen] = useState(false);
  const [allCommands, setAllCommands] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState(null);

  const fetchAllCommands = useCallback(async () => {
    setAllLoading(true);
    setAllError(null);
    try {
      const res = await authService.authenticatedRequest(SERVER_URL + '/v1/robot/all-commands');
      const data = await res.json();
      setAllCommands(Array.isArray(data) ? data : []);
    } catch (e) {
      setAllError(e.message);
      setAllCommands([]);
    } finally {
      setAllLoading(false);
    }
  }, []);

  const handleOpenAllModal = useCallback(async () => {
    setIsAllModalOpen(true);
    await fetchAllCommands();
  }, [fetchAllCommands]);

  const handleCloseAllModal = useCallback(() => {
    setIsAllModalOpen(false);
  }, []);

  const handleCommandRowClick = useCallback((cmd) => {
    if (!cmd?.side || !cmd.stock || !cmd.futures) return;

    const detail = {
      side: cmd.side,
      stock: cmd.stock,
      futures: cmd.futures,
      broker: cmd.broker,
    };
    const targetPath = cmd.side === 'enter' ? '/' : '/deals';

    if (location.pathname === targetPath) {
      window.dispatchEvent(new CustomEvent(ROBOT_COMMAND_SELECT_EVENT, { detail }));
      return;
    }

    navigate(targetPath, { state: { robotCommandSelect: detail } });
  }, [location.pathname, navigate]);

  const handleSelectCommand = useCallback((cmd) => {
    setSelectedCommandId((currentId) => (currentId === cmd.id ? null : cmd.id));
  }, []);

  const selectedCommand = useMemo(
    () => commands.find((command) => command.id === selectedCommandId) ?? null,
    [commands, selectedCommandId],
  );

  useEffect(() => {
    if (selectedCommandId && !commands.some((command) => command.id === selectedCommandId)) {
      setSelectedCommandId(null);
    }
  }, [commands, selectedCommandId]);

  const containerStyle =
    variant === 'embedded' ? { ...styles.container, ...styles.containerEmbedded } : styles.container;

  if (commands.length === 0 && !showWhenEmpty) return null;

  const tableCardClassName = `robot-commands-table${variant === 'embedded' ? ' robot-commands-table--embedded' : ''}${mobileSelectionLayout ? ' robot-commands-table--mobile-selection' : ''}`;

  const tableCardContent = (
    <>
      <div className="robot-cmd-panel-header">
        <span>Автосделки</span>
        {!mobileSelectionLayout && (
          <button
            type="button"
            className="robot-cmd-header-link"
            title="Архив"
            onClick={handleOpenAllModal}
          >
            <LinkIcon />
          </button>
        )}
      </div>
      {error && (
        <div style={styles.error}>{error}</div>
      )}
      <RobotCommandsGrid
        commands={commands}
        actionLoading={actionLoading}
        runAction={runAction}
        onCommandRowClick={mobileSelectionLayout ? undefined : handleCommandRowClick}
        onSelectCommand={mobileSelectionLayout ? handleSelectCommand : undefined}
        onNavigateCommand={mobileSelectionLayout ? handleCommandRowClick : undefined}
        selectedCommandId={mobileSelectionLayout ? selectedCommandId : null}
        selectionMode={mobileSelectionLayout}
        bodyMaxHeight={bodyMaxHeight}
        groupByBroker={groupByBroker}
        fluidColumns={fluidColumns}
        mergePairColumn={mergePairColumn}
        showStatusColumn={mobileSelectionLayout}
      />
    </>
  );

  return (
    <>
      {mobileSelectionLayout ? (
        <div className="robot-cmd-mobile-block">
          <div className={tableCardClassName}>
            {tableCardContent}
          </div>
          <div className="robot-cmd-table-footer">
            <RobotCommandStatusLegend />
            <RobotCommandActionBar
              command={selectedCommand}
              actionLoading={actionLoading}
              runAction={runAction}
            />
          </div>
        </div>
      ) : (
        <div className={tableCardClassName} style={containerStyle}>
          {tableCardContent}
        </div>
      )}

      <RobotCommandsAllModal
        isOpen={isAllModalOpen}
        onClose={handleCloseAllModal}
        commands={allCommands}
        loading={allLoading}
        error={allError}
      />
    </>
  );
}

const styles = {
  container: {
    position: 'fixed',
    left: 'max(20px, calc(50% - 650px - 680px - 12px))',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '680px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1000,
    maxHeight: '90vh',
  },
  containerEmbedded: {
    position: 'relative',
    left: 'auto',
    top: 'auto',
    transform: 'none',
    width: '100%',
    maxWidth: '680px',
    marginLeft: 'auto',
    marginRight: 'auto',
    flexShrink: 0,
    zIndex: 1,
    maxHeight: 'min(90vh, 480px)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: '#667eea',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  headerLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    borderRadius: '4px',
    padding: '2px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  modalMessage: {
    padding: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.85rem',
    textAlign: 'center',
  },
  error: {
    padding: '12px 16px',
    color: '#f44336',
    fontSize: '0.85rem',
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
  },
  tableWrapper: {
    flex: 1,
    overflowX: 'auto',
    overflowY: 'hidden',
    minHeight: 0,
  },
  tableBodyWrapper: {
    maxHeight: '130px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8rem',
    tableLayout: 'fixed',
  },
  th: {
    padding: '5px 8px',
    textAlign: 'center',
    fontWeight: '500',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  trError: {
    backgroundColor: 'rgba(244, 67, 54, 0.14)',
    boxShadow: 'inset 0 0 0 1px rgba(244, 67, 54, 0.25)',
  },
  td: {
    padding: '3px 8px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    textAlign: 'right',
  },
  tdCreatedAt: {
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    padding: '2px',
    margin: '0 1px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
  },
  actionsCell: {
    whiteSpace: 'nowrap',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
};

export default RobotCommandsTable;
